export const MAINTENANCE_PAGES_SETTING = 'maintenance_pages';
export const LEGACY_MAINTENANCE_SETTING = 'maintenance_mode';

// Keep this registry in sync whenever a public route is added to App.js.
// Dynamic routes are grouped with the public page they belong to.
export const MAINTENANCE_PAGES = [
  {
    id: 'home',
    label: 'Home',
    description: 'Landing page and all landing-page sections.',
    paths: ['/']
  },
  {
    id: 'news',
    label: 'News',
    description: 'Public news listing and announcements.',
    paths: ['/news']
  },
  {
    id: 'careers',
    label: 'Careers',
    description: 'Career listings and application entry points.',
    paths: ['/careers']
  },
  {
    id: 'applications',
    label: 'Applications',
    description: 'Individual job application forms and shared application links.',
    prefixes: ['/application']
  },
  {
    id: 'partners',
    label: 'Partners',
    description: 'Veloura partner directory.',
    paths: ['/partners']
  },
  {
    id: 'studios',
    label: 'Studios',
    description: 'Veloura Studios and linked studio galleries.',
    prefixes: ['/studios']
  },
  {
    id: 'team',
    label: 'Team',
    description: 'Team directory and individual profiles.',
    prefixes: ['/team']
  },
  {
    id: 'publications',
    label: 'Publications',
    description: 'Publication library.',
    paths: ['/publications']
  },
  {
    id: 'publication-reader',
    label: 'Publication Reader',
    description: 'Flipbooks, magazines, blogs, and articles.',
    prefixes: ['/publication']
  },
  {
    id: 'shop',
    label: 'Shop',
    description: 'Shop catalogue and individual products.',
    prefixes: ['/shop']
  },
  {
    id: 'checkout',
    label: 'Checkout',
    description: 'Shopping-cart checkout.',
    paths: ['/checkout']
  },
  {
    id: 'payment-gateway',
    label: 'Payment Gateway',
    description: 'Order payment processing page.',
    paths: ['/paymentgateway']
  },
  {
    id: 'order-confirmation',
    label: 'Order Confirmation',
    description: 'Order confirmation pages.',
    prefixes: ['/confirmation']
  },
  {
    id: 'purchase-complete',
    label: 'Purchase Complete',
    description: 'Completed-purchase download page.',
    prefixes: ['/purchase-complete']
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Signed-in user account area.',
    paths: ['/account']
  },
  {
    id: 'shared-gallery',
    label: 'Shared Gallery',
    description: 'Publicly shared gallery pages.',
    prefixes: ['/gallery']
  }
];

const normalizePath = (pathname = '/') => {
  const normalized = String(pathname).split('?')[0].split('#')[0].toLowerCase();
  if (!normalized || normalized === '/') return '/';
  return normalized.replace(/\/+$/, '');
};

const matchesPrefix = (pathname, prefix) => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

export const isMaintenanceExemptPath = (pathname) => {
  const normalizedPath = normalizePath(pathname);
  return ['/admin', '/auth/callback', '/maintenance'].some(
    (prefix) => matchesPrefix(normalizedPath, prefix)
  );
};

export const getMaintenancePageForPath = (pathname) => {
  const normalizedPath = normalizePath(pathname);

  if (isMaintenanceExemptPath(normalizedPath)) return null;

  const matchedPage = MAINTENANCE_PAGES.find((page) => (
    page.paths?.includes(normalizedPath)
      || page.prefixes?.some((prefix) => matchesPrefix(normalizedPath, prefix))
  ));

  // App.js currently renders unmatched public routes as the landing page.
  return matchedPage || MAINTENANCE_PAGES.find((page) => page.id === 'home');
};

export const createMaintenanceState = (enabled = false) => (
  MAINTENANCE_PAGES.reduce((state, page) => {
    state[page.id] = Boolean(enabled);
    return state;
  }, {})
);

const parseBoolean = (value) => value === true || value === 'true' || value === '1';

export const parseMaintenanceState = (value, legacyValue = false) => {
  let parsedValue = value;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return createMaintenanceState(false);
    }

    if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
      try {
        parsedValue = JSON.parse(trimmedValue);
      } catch (error) {
        // The global request sanitizer removed JSON quotes from earlier saves.
        parsedValue = trimmedValue
          .slice(1, -1)
          .split(',')
          .reduce((state, entry) => {
            const separatorIndex = entry.indexOf(':');
            if (separatorIndex === -1) return state;

            const key = entry.slice(0, separatorIndex).trim();
            const entryValue = entry.slice(separatorIndex + 1).trim();
            if (MAINTENANCE_PAGES.some((page) => page.id === key)) {
              state[key] = parseBoolean(entryValue);
            }
            return state;
          }, {});
      }
    } else {
      const enabledPageIds = new Set(
        trimmedValue.split(',').map((pageId) => pageId.trim()).filter(Boolean)
      );
      return MAINTENANCE_PAGES.reduce((state, page) => {
        state[page.id] = enabledPageIds.has(page.id);
        return state;
      }, {});
    }
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    return createMaintenanceState(parseBoolean(legacyValue));
  }

  return MAINTENANCE_PAGES.reduce((state, page) => {
    state[page.id] = parseBoolean(parsedValue[page.id]);
    return state;
  }, {});
};

export const serializeMaintenanceState = (state) => MAINTENANCE_PAGES
  .filter((page) => Boolean(state?.[page.id]))
  .map((page) => page.id)
  .join(',');
